import type { FilterState } from '@controlrooms/components/src/table/types';

export enum ViewMode {
  STACK = 'stack',
  GRID = 'grid',
}

export enum Mode {
  LIVE = 'live',
  STATIC = 'static',
}

export enum AlertType {
  ALL = 'all',
  ANOMALY = 'anomaly',
  LIMIT = 'limit',
}

export enum ModelType {
  ALL = 'all',
  MULTIVARIATE = 'multivariate',
  UNIVARIATE = 'univariate',
}

export enum AlertStatus {
  ALL = 'all',
  ACTIVE = 'active',
  SNOOZED = 'snoozed',
  ARCHIVED = 'archived',
}

export interface TimePreset {
  label: string;
  displayValue: string;
  value: string;
}

export interface LimitFilters {
  limits: boolean;
  annotations: boolean;
  HH: boolean;
  H: boolean;
  L: boolean;
  LL: boolean;
  limitGuideLines: boolean;
}

// Alert Board Configuration Types
export interface ColumnSetting {
  key: string;
  visible: boolean;
  width: number;
  order: number;
  sortDirection: 'none' | 'asc' | 'desc';
}

export interface TimeSelectionConfig {
  startTime: string;
  endTime: string;
  timezone: string;
  streamingTimeInSeconds?: number;
  timeRange: string;
  timePreset?: string;
}

// Simplified tag structure for saving in config
export interface SavedTag {
  folder: number;
  name: string;
}

export interface AlertFiltersConfig {
  alertType: AlertType;
  alertStatus: AlertStatus;
  modelType: ModelType;
  tags: SavedTag[];
  systems: number[];
}

export interface TableConfig {
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  columnSettings: ColumnSetting[];
  columnFilters?: FilterState;
}

/**
 * Configuration for a single saved view within an alert board.
 * This is what gets persisted to the API for each view.
 */
export interface AlertBoardViewConfig {
  name: string;
  is_default: boolean;
  viewMode: ViewMode;
  displayMode: Mode;
  timeSelection: TimeSelectionConfig;
  filters: AlertFiltersConfig;
  tableConfig: TableConfig;
}

/**
 * Full runtime configuration including board metadata.
 * Used internally in the context for tracking current state.
 * Extends AlertBoardViewConfig with board-specific properties.
 */
export interface AlertBoardConfig extends AlertBoardViewConfig {
  rule_board_id: string;
  rule_board_name: string;
  description?: string;
  user_id?: number;
}

/**
 * API persistence structure for a single alert board's settings.
 * Contains an array of saved views.
 */
export interface AlertBoardSettings {
  views: AlertBoardViewConfig[];
}
